/**
 * Sound identity keys — what makes two `sounds` rows "the same sound".
 *
 * Background (TES-1616 / TES-1729): the original `sounds.dedup_key` was a
 * slugified TITLE. Titles are the least stable thing about a scraped sound —
 * the weekly curation pass rewrites them ("Coin Mario" -> "Mario Coin Collect
 * (Super Mario Bros.)"), so the next re-scrape of the byte-identical file
 * computed a *different* dedup_key, sailed past both the partial unique index
 * and the ingest-time RPC, and inserted a second active row pointing at the
 * same audio. 105 live duplicate `file_url` groups in prod came from exactly
 * this (TES-1617 tracks cleaning them up).
 *
 * So identity is derived from the FILE, not the title:
 *
 *   - `soundFileKey(file_url)` — normalized storage-object name. Stable
 *     across title rewrites AND across category re-classification (the
 *     category only changes the object's *prefix*, so keying on the basename
 *     catches the re-upload that a full-URL comparison misses). Objects stored
 *     under a per-sound directory key on '<dir>/<basename>' instead — see the
 *     TES-1742 note on `soundFileKey`.
 *   - `sourceAudioSha256` (see scripts/lib/audio-content-hash.ts) — hash of
 *     the pre-processing source bytes. Catches the same audio arriving under a
 *     new filename, which the file key cannot.
 *   - `soundTitleDedupKey(title)` — kept as a secondary signal, and exported
 *     here so the ingest scripts stop rolling their own slightly-different
 *     normalization (the old script-local `normKey` did NOT strip parenthesized
 *     suffixes the way the DB's generated column does, which is how
 *     "Gaster's Theme" got in alongside "Gaster's Theme (Undertale)").
 *
 * These MUST stay byte-for-byte equivalent to their SQL counterparts in
 * supabase/migrations/20260818_dedup_file_identity.sql
 * (`public.sound_file_dedup_key`, `public.sound_title_dedup_key`). If you
 * change the normalization here, change it there in the same PR — the DB
 * trigger and this module have to agree or ingest will disagree with the
 * constraint that backs it.
 *
 * This module is intentionally dependency-free (no node:crypto, no supabase)
 * so it is safe to import from anywhere, including client bundles.
 */

/** Extensions stripped before normalizing, so `foo.mp3` and `foo.wav` agree. */
const AUDIO_EXTENSION_RE = /\.(wav|mp3|m4a|ogg|aac|flac)$/

/** The Supabase storage bucket every hosted sound lives in. */
const AUDIO_BUCKET = 'audio-files'

/**
 * The storage-object path inside `audio-files`, or `null` when the input is not
 * a path into our bucket (an external URL we do not control the layout of).
 *
 * Mirrors the SQL `CASE` in `public.sound_file_dedup_key`:
 *   1. contains a whole `audio-files/` segment -> everything after the LAST one
 *   2. no scheme -> already a bare storage path / filename, use as-is
 *   3. otherwise -> a foreign URL; we cannot read structure out of it
 */
function bucketObjectPath(path: string): string | null {
  const segments = path.split('/')
  const bucketAt = segments.lastIndexOf(AUDIO_BUCKET)
  if (bucketAt >= 0) return segments.slice(bucketAt + 1).join('/')
  if (!path.includes('://')) return path
  return null
}

/**
 * Normalized identity key for a sound's audio object.
 *
 * Accepts a full public URL, a bare storage path, or a plain filename — all
 * three reduce to the same key, which is what lets an ingest script check the
 * key BEFORE uploading (it only has the filename at that point) and have the
 * answer match the `file_url` the row will end up with.
 *
 *   https://…/audio-files/games/coin_mario.wav -> 'coinmario'
 *   sound-effects/Coin_Mario.WAV               -> 'coinmario'
 *   coin_mario.mp3                             -> 'coinmario'
 *
 * TWO STORAGE CONVENTIONS, TWO IDENTITY RULES (TES-1742). The weekly pipeline
 * writes flat objects — `<category>/<scraped_filename>.wav` — where the
 * basename IS the identity and the prefix is just the (re-classifiable)
 * category. But one 2026-04-27 batch wrote nested objects —
 * `sound-effects/<slug>/LockChime.wav` — where the identity lives in the
 * DIRECTORY and every basename is the same canonical Tesla filename. Keying
 * those on the basename alone collapsed 7 genuinely different sounds (Coin
 * Drop, Glass Clink, Light Switch, Match Strike, Microwave Done, Typewriter
 * Bell, Vinyl Scratch Stop) onto the single key 'lockchime', which would have
 * made the DB guard reject the next legitimate upload to `<slug>/LockChime.wav`
 * as a duplicate of an unrelated sound.
 *
 * So: an object with a per-sound directory (>=3 segments inside `audio-files`)
 * keys on its LAST TWO segments; everything else keys on the basename exactly
 * as before. The flat case is deliberately byte-identical to the pre-TES-1742
 * behaviour, so the 3,003 flat rows keep their existing key and only the 16
 * nested rows are re-keyed.
 *
 *   …/audio-files/sound-effects/coin-spin-drop/LockChime.wav
 *                                       -> 'coinspindroplockchime'
 *   sound-effects/coin-spin-drop/LockChime.wav
 *                                       -> 'coinspindroplockchime'
 *
 * Returns '' when there is nothing usable to key on; callers must treat a
 * short/empty key as "no opinion" rather than as a match (the same way the
 * DB-side guard skips keys shorter than 2 characters).
 *
 * Note: percent-escapes are deliberately NOT decoded, to stay identical to the
 * SQL implementation. `%20` normalizes to the digits `20`, so an encoded and an
 * unencoded spelling of the same object can miss each other. That direction of
 * error (a missed duplicate) is the safe one; the content hash is the backstop.
 */
export function soundFileKey(fileUrl: string | null | undefined): string {
  if (!fileUrl) return ''
  // Drop query string and fragment.
  const path = fileUrl.split('?')[0].split('#')[0]
  const segments = path.split('/')

  // Default identity: the basename, i.e. everything after the last '/'.
  let identity = segments[segments.length - 1]

  // Nested per-sound directory -> the directory carries the identity, so key on
  // '<dir>/<basename>'. Only inside our own bucket: a foreign URL's directory
  // structure is not ours to interpret.
  const objectPath = bucketObjectPath(path)
  if (objectPath !== null && objectPath.split('/').length >= 3) {
    identity = `${segments[segments.length - 2]}/${identity}`
  }

  return identity.toLowerCase().replace(AUDIO_EXTENSION_RE, '').replace(/[^a-z0-9]+/g, '')
}

/**
 * Normalized title key — mirrors the `sounds.dedup_key` generated column:
 * lowercase, parenthesized segments removed, non-alphanumerics stripped.
 *
 *   "Mario Coin Collect (Super Mario Bros.)" -> 'mariocoincollect'
 */
export function soundTitleDedupKey(title: string | null | undefined): string {
  if (!title) return ''
  return title
    .toLowerCase()
    .replace(/\([^)]*\)/g, '')
    .replace(/[^a-z0-9]+/g, '')
}

/**
 * Title key WITHOUT the parenthesized-segment strip.
 *
 * The DB's generated `dedup_key` drops "(…)" segments, which is right for
 * catching a curated rename but means "Alarm (short)" and "Alarm (long)"
 * collapse together. Ingest checks both spellings so neither normalization's
 * blind spot lets a duplicate through, and neither one's over-eagerness is the
 * only thing standing between a distinct sound and the catalog.
 */
export function soundTitleRawKey(title: string | null | undefined): string {
  if (!title) return ''
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '')
}

/** The minimum key length the DB-side guard considers meaningful. */
export const MIN_IDENTITY_KEY_LENGTH = 2

/** True when a key is long enough to be worth matching on. */
export function isUsableIdentityKey(key: string): boolean {
  return key.length >= MIN_IDENTITY_KEY_LENGTH
}

/**
 * Filename or storage path a Create listing can send as file identity.
 * `LockChime.wav` is the Tesla export name for every download, so it must not
 * be treated as a library object key (TES-1742).
 */
export function listingFileIdentityInput(filename: string): string | undefined {
  const trimmed = filename.trim()
  if (!trimmed) return undefined
  const key = soundFileKey(trimmed)
  if (!isUsableIdentityKey(key)) return undefined
  if (key === 'lockchime') return undefined
  return trimmed
}
